import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider } from "./hooks/useAuth";
import { usePageTracking } from "./hooks/usePageTracking";

// Lazy load ALL pages including Index for better initial bundle
const Index = lazy(() => import("./pages/Index"));

// Lazy load admin components to reduce initial bundle
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminLeads = lazy(() => import("./pages/admin/Leads"));
const AdminBlogs = lazy(() => import("./pages/admin/Blogs"));
const AdminBlogEditor = lazy(() => import("./pages/admin/BlogEditor"));

// Lazy load non-critical pages
const About = lazy(() => import("./pages/About"));
const Rooms = lazy(() => import("./pages/Rooms"));
const RoomDetail = lazy(() => import("./pages/RoomDetail"));
const Dining = lazy(() => import("./pages/Dining"));
const Safari = lazy(() => import("./pages/Safari"));
const Experiences = lazy(() => import("./pages/Experiences"));
const ExperienceJungle = lazy(() => import("./pages/ExperienceJungle"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Install = lazy(() => import("./pages/Install"));
const FontsShowcase = lazy(() => import("./pages/FontsShowcase"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Minimal loading component for fast initial paint
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: 'hsl(90 10% 96%)' }}>
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-2" style={{ borderColor: 'hsl(120 25% 25%)', borderTopColor: 'transparent' }} />
      <p className="text-sm" style={{ color: 'hsl(30 5% 25%)' }}>Loading...</p>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: false,
    },
  },
});

// Page tracking wrapper component
const PageTracker = ({ children }: { children: React.ReactNode }) => {
  usePageTracking();
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <PageTracker>
            <Suspense fallback={<PageLoader />}>
              <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/about" element={<About />} />
              <Route path="/rooms" element={<Rooms />} />
              <Route path="/rooms/:id" element={<RoomDetail />} />
              <Route path="/dining" element={<Navigate to="/experiences" replace />} />
              <Route path="/safari" element={<Safari />} />
              <Route path="/experiences" element={<Experiences />} />
              <Route path="/experience-jungle" element={<ExperienceJungle />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:id" element={<BlogPost />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/install" element={<Install />} />
              <Route path="/fonts" element={<FontsShowcase />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="leads" element={<AdminLeads />} />
                <Route path="blogs" element={<AdminBlogs />} />
                <Route path="blogs/:id/edit" element={<AdminBlogEditor />} />
              </Route>
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </PageTracker>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
